import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LocationOn,
  AccessTime,
  CheckCircle,
  Lightbulb,
  Support,
  Comment,
} from '@mui/icons-material';
import { useConfig, useConfigSection } from '../utils/useConfig';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import Seo from '../components/seo/Seo';

function About() {
  const config = useConfig();
  const social = useConfigSection('social');

  // Konfiguracja Facebook SDK
  useEffect(() => {
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src =
        'https://connect.facebook.net/pl_PL/sdk.js#xfbml=1&version=v12.0';
      document.body.appendChild(script);
    }
  }, [config.company.name]);

  // Animacje
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Dane o firmie
  const companyValues = [
    {
      icon: <CheckCircle />,
      title: 'Jakość',
      description:
        'Oferujemy wyłącznie produkty od sprawdzonych producentów, które spełniają najwyższe standardy bezpieczeństwa i niezawodności.',
    },
    {
      icon: <Support />,
      title: 'Obsługa',
      description:
        'Nasz zespół ekspertów zapewnia profesjonalne doradztwo, aby pomóc klientom w wyborze odpowiednich rozwiązań.',
    },
    {
      icon: <Comment />,
      title: 'Rzetelność',
      description:
        'Dotrzymujemy obietnic. Terminowa realizacja zamówień i transparentne warunki współpracy to nasze priorytety.',
    },
    {
      icon: <Lightbulb />,
      title: 'Innowacyjność',
      description:
        'Śledzimy najnowsze trendy w branży elektrycznej, aby nasi klienci mieli dostęp do nowoczesnych rozwiązań.',
    },
  ];

  // Historia firmy
  const companyHistory = config.company.history;

  // Przygotowanie listy mediów społecznościowych
  const socialLinks = [
    ...(social.facebook
      ? [
          {
            name: 'Facebook',
            url: social.facebook,
            icon: <FacebookIcon />,
            color: 'hover:bg-blue-600',
          },
        ]
      : []),
    ...(social.instagram
      ? [
          {
            name: 'Instagram',
            url: social.instagram,
            icon: <InstagramIcon />,
            color: 'hover:bg-pink-600',
          },
        ]
      : []),
    ...(social.youtube
      ? [
          {
            name: 'YouTube',
            url: social.youtube,
            icon: <YouTubeIcon />,
            color: 'hover:bg-red-600',
          },
        ]
      : []),
    ...(social.twitter
      ? [
          {
            name: 'Twitter',
            url: social.twitter,
            icon: <TwitterIcon />,
            color: 'hover:bg-blue-400',
          },
        ]
      : []),
    ...(social.linkedin
      ? [
          {
            name: 'LinkedIn',
            url: social.linkedin,
            icon: <LinkedInIcon />,
            color: 'hover:bg-blue-800',
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="bg-white min-h-screen">
        {/* Hero section z tłem */}
        <div
          className="relative bg-cover bg-center h-[60vh] flex items-center"
          style={{ backgroundImage: `url('/images/building.jpg')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black to-transparent opacity-80"></div>

          <div className="relative z-10 container mx-auto px-4 lg:px-8">
            <motion.div
              className="max-w-3xl"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                {config.company.name}
              </h1>
              <div className="w-24 h-1 bg-primary mb-6"></div>
              <p className="text-xl text-white/90 mb-6">
                Od ponad 25 lat dostarczamy najwyższej jakości osprzęt
                elektro-energetyczny. Nasza wiedza i doświadczenie są gwarancją
                profesjonalnych rozwiązań dla Twojego biznesu.
              </p>
            </motion.div>
          </div>
        </div>

        {/* O firmie */}
        <motion.section
          className="py-16 bg-gradient-to-b from-gray-50 to-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="w-full md:w-1/2">
                <motion.img
                  src="/images/inside-shop.jpg"
                  alt={`Wnętrze sklepu ${config.company.name}`}
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              {config.company.about.longInfo && (
                <div className="w-full md:w-1/2">
                  <h2 className="text-3xl font-bold mb-6 text-gray-900">
                    O naszej firmie
                  </h2>
                  <div className="w-16 h-1 bg-primary mb-6"></div>

                  {config.company.about.longInfo.map((paragraph) => (
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {paragraph.section}
                    </p>
                  ))}

                  <div className="flex gap-4 items-center">
                    <div className="flex items-center text-gray-700">
                      <LocationOn className="text-primary mr-2" />
                      <span>{`${config.contact.address.street}, ${config.contact.address.postal} ${config.contact.address.city}`}</span>
                    </div>

                    <div className="flex items-center text-gray-700">
                      <AccessTime className="text-primary mr-2" />
                      <span>Od {config.company.founded} roku</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.section>

        {/* Nasze wartości */}
        <motion.section className="py-16 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                Nasze wartości
              </h2>
              <div className="w-16 h-1 bg-primary mb-6 mx-auto"></div>
              <p className="max-w-2xl mx-auto text-gray-700">
                Wartości, które wyznajemy, są fundamentem jakości naszych usług
                i relacji z klientami. To one pozwalają nam stale się rozwijać i
                dostarczać najlepsze rozwiązania.
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {companyValues.map((value, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow border border-gray-100"
                  variants={fadeIn}
                  whileHover={{ y: -5 }}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 text-primary">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {companyHistory && companyHistory.length > 0 && (
          <section className="py-16 bg-gradient-to-b from-white to-gray-50">
            <div className="container mx-auto px-4 lg:px-8">
              <motion.div
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl font-bold mb-4 text-gray-900">
                  Nasza historia
                </h2>
                <div className="w-16 h-1 bg-primary mb-6 mx-auto"></div>
                <p className="max-w-2xl mx-auto text-gray-700">
                  Droga, którą przeszliśmy od założenia firmy do dziś, jest
                  pełna ważnych momentów i ciągłego rozwoju. Poznaj
                  najważniejsze etapy naszej historii.
                </p>
              </motion.div>

              <div className="max-w-4xl mx-auto">
                {companyHistory.map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex mb-8 relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                  >
                    {/* Linia czasowa */}
                    {index !== companyHistory.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-300"></div>
                    )}

                    {/* Rok i marker */}
                    <div className="mr-8 flex flex-col items-center">
                      <div className="bg-primary text-white py-1 px-3 rounded font-bold text-sm mb-2">
                        {item.year}
                      </div>
                      <div className="w-3 h-3 rounded-full bg-primary border-4 border-primary/20 z-10"></div>
                    </div>

                    {/* Treść */}
                    <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100 flex-grow">
                      <h3 className="font-semibold text-lg mb-2 text-gray-900">
                        {item.title}
                      </h3>
                      <p className="text-gray-700">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Media społecznościowe */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold mb-4 text-gray-900">
                Śledź nas w mediach
              </h2>
              <div className="w-16 h-1 bg-primary mb-6 mx-auto"></div>
              <p className="max-w-2xl mx-auto text-gray-700">
                Bądź na bieżąco z naszymi najnowszymi produktami, promocjami i
                poradami branżowymi. Odwiedź i obserwuj nasze profile w mediach
                społecznościowych!
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {socialLinks.map((socialLink, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div
                    className={`text-white p-4 flex items-center justify-between ${socialLink.color.replace(
                      'hover:',
                      '',
                    )}`}
                  >
                    <h3 className="font-semibold text-lg">{socialLink.name}</h3>
                    <a
                      href={socialLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:underline text-sm"
                    >
                      Odwiedź profil →
                    </a>
                  </div>
                  <div className="p-4 h-[500px] flex items-center justify-center bg-gray-50">
                    {socialLink.name === 'Facebook' && (
                      <div
                        className="fb-page w-full h-full"
                        data-href={socialLink.url}
                        data-tabs="timeline"
                        data-width="500"
                        data-height="470"
                        data-small-header="false"
                        data-adapt-container-width="true"
                        data-hide-cover="false"
                        data-show-facepile="true"
                      >
                        <blockquote
                          cite={socialLink.url}
                          className="fb-xfbml-parse-ignore"
                        >
                          <a href={socialLink.url}>{config.company.name}</a>
                        </blockquote>
                      </div>
                    )}
                    {socialLink.name === 'Instagram' && (
                      <iframe
                        title="Instagram"
                        src={`${socialLink.url.replace(
                          'https://www.instagram.com/',
                          'https://www.instagram.com/',
                        )}/embed`}
                        frameBorder="0"
                        scrolling="no"
                        allowTransparency={true}
                        className="w-full h-full"
                      ></iframe>
                    )}
                    {socialLink.name === 'YouTube' && (
                      <iframe
                        title="YouTube"
                        src="https://www.youtube.com/embed/PspfBeckXXg?si=LRSPBvg9n8Bh18ju"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      ></iframe>
                    )}
                    {socialLink.name !== 'Facebook' &&
                      socialLink.name !== 'Instagram' &&
                      socialLink.name !== 'YouTube' && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                              {socialLink.icon}
                            </div>
                            <p className="text-lg font-semibold">
                              {socialLink.name}
                            </p>
                            <a
                              href={socialLink.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-block text-primary hover:underline"
                            >
                              Odwiedź nasz profil
                            </a>
                          </div>
                        </div>
                      )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <motion.section
          className="py-16 bg-gradient-to-r from-primary to-blue-700 text-white"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <motion.h2
              className="text-3xl font-bold mb-6"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
            >
              Potrzebujesz profesjonalnych produktów elektrycznych?
            </motion.h2>

            <motion.p
              className="mb-8 max-w-2xl mx-auto text-white/90"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Skontaktuj się z nami już dziś, aby omówić swoje potrzeby lub
              odwiedź nasz sklep stacjonarny.
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4 justify-center"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <a
                href="/kontakt"
                className="bg-white text-primary font-medium py-3 px-6 rounded-md hover:bg-gray-100 transition-colors"
              >
                Skontaktuj się z nami
              </a>
              <a
                href="/sklep"
                className="bg-transparent border-2 border-white text-white font-medium py-3 px-6 rounded-md hover:bg-white/10 transition-colors"
              >
                Przejdź do sklepu
              </a>
            </motion.div>
          </div>
        </motion.section>
      </div>
      <Seo title={`O nas`}></Seo>
    </>
  );
}

export default About;
